import { useMemo, useState } from 'react'
import { useForm } from '@inertiajs/react'
import { Check, Copy, CreditCard, Gift, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { BillingPlanOption, PaymentLink, WorkerSubscription } from '../types'

interface Props {
  workerId: string
  billing: WorkerSubscription
  plans: BillingPlanOption[]
  paymentLink: PaymentLink | null
}

const STATUS_LABELS: Record<string, string> = {
  incomplete: 'Aguardando pagamento',
  incomplete_expired: 'Checkout expirado',
  trialing: 'Período de teste',
  active: 'Ativa',
  past_due: 'Pagamento pendente',
  paused: 'Pausada',
  canceled: 'Cancelada',
  unpaid: 'Inadimplente',
}

const BLOCKED_PAYMENT_LINK_STATUSES = new Set(['incomplete', 'paused', 'unpaid'])

function formatExpiry(expiresAt: string | null) {
  if (!expiresAt) return null

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(expiresAt))
}

export default function GrantCompedSubscriptionSection({ workerId, billing, plans, paymentLink }: Props) {
  const orderedPlans = useMemo(() => plans.filter((plan) => plan.published), [plans])
  const defaultSlug = paymentLink?.plan_slug || billing.plan_slug || orderedPlans[0]?.slug || plans[0]?.slug || ''
  const form = useForm({ plan_slug: defaultSlug })
  const selectedPlan = plans.find((plan) => plan.slug === form.data.plan_slug)
  const matchingPaymentLink = paymentLink?.plan_slug === form.data.plan_slug ? paymentLink : null
  const recoveringPastDue = billing.source === 'stripe' && billing.status === 'past_due'
  const paymentLinkBlocked = billing.source === 'stripe' && (
    BLOCKED_PAYMENT_LINK_STATUSES.has(billing.status ?? '') ||
    (recoveringPastDue && selectedPlan?.slug !== billing.plan_slug)
  )
  const activeStripeBilling = billing.source === 'stripe' && billing.active
  const [copied, setCopied] = useState(false)
  const expiry = formatExpiry(matchingPaymentLink?.expires_at ?? null)

  const handleGrant = () => {
    if (!selectedPlan) return
    form.post(`/cc/digital_workers/${workerId}/grant_comped_subscription`, { preserveScroll: true })
  }

  const handlePaymentLink = () => {
    if (!selectedPlan?.purchasable) return
    form.post(`/cc/digital_workers/${workerId}/payment_link`, { preserveScroll: true })
  }

  const handleCopy = async () => {
    if (!matchingPaymentLink?.checkout_url) return

    try {
      await navigator.clipboard.writeText(matchingPaymentLink.checkout_url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="mt-3 rounded-md border border-border bg-muted/40 p-3">
      <div className="mb-2 flex items-center gap-2">
        <Gift className="h-3.5 w-3.5 text-emerald-600" />
        <span className="text-xs font-semibold text-foreground">Cobrança</span>
      </div>
      <div className="mb-3 text-xs text-muted-foreground">
        {billing.active
          ? `${billing.plan_name ?? 'Plano'} · ${billing.source === 'manual' ? 'Cortesia' : 'Stripe'}`
          : billing.status
          ? STATUS_LABELS[billing.status] || billing.status
          : 'Sem assinatura ativa'}
      </div>
      <div className="space-y-2">
        <select
          value={form.data.plan_slug}
          onChange={(event) => form.setData('plan_slug', event.target.value)}
          disabled={form.processing || plans.length === 0}
          className="w-full rounded border border-border bg-background px-2 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          aria-label="Plano de cobrança"
        >
          {plans.map((plan) => (
            <option key={plan.slug} value={plan.slug}>
              {plan.name}{plan.published ? '' : ' (oculto)'}
            </option>
          ))}
        </select>
        <Button
          type="button"
          size="sm"
          variant={billing.source === 'manual' && billing.active ? 'outline' : 'default'}
          className="w-full"
          onClick={handleGrant}
          disabled={form.processing || !selectedPlan}
        >
          {form.processing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Gift className="h-3.5 w-3.5" />}
          Conceder cortesia
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="w-full"
          onClick={handlePaymentLink}
          disabled={form.processing || !selectedPlan?.purchasable || activeStripeBilling || paymentLinkBlocked}
        >
          {form.processing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CreditCard className="h-3.5 w-3.5" />}
          Copiar link de pagamento
        </Button>
        {matchingPaymentLink?.checkout_url && (
          <div className="space-y-2 rounded border border-emerald-500/30 bg-emerald-500/10 p-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                Link pronto{expiry ? ` · expira em ${expiry}` : ''}
              </span>
              <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={handleCopy}>
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Copiado' : 'Copiar'}
              </Button>
            </div>
            <input
              readOnly
              value={matchingPaymentLink.checkout_url}
              onFocus={(event) => event.currentTarget.select()}
              className="w-full rounded border border-border bg-background px-2 py-1.5 text-[11px] text-foreground"
              aria-label="Link de pagamento gerado"
            />
          </div>
        )}
      </div>
    </div>
  )
}
